import React from 'react';
import { useTranslation } from 'react-i18next';
import { PanelLeft } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { LAYOUT } from '@constants/layout';
import { useNavSections } from '@hooks/useNavSections';
import NavSection from './NavSection';
import RevyMark from './RevyMark';

interface SidebarPanelProps {
  collapsed: boolean;
  /** Mobile drawer passes this so tapping a link also dismisses the sheet. */
  onNavigate?: () => void;
  /** Desktop rail passes this so the control stays next to the rail it changes. */
  onToggle?: () => void;
}

/** Shared body for both the desktop rail and the mobile drawer. */
const SidebarPanel: React.FC<SidebarPanelProps> = ({ collapsed, onNavigate, onToggle }) => {
  const { t } = useTranslation();
  const { sections, footer } = useNavSections();

  return (
    <div className="flex h-full flex-col bg-card">
      <div
        className={cn(
          'flex shrink-0 items-center',
          collapsed ? 'justify-center' : 'justify-between gap-2 px-3',
        )}
        style={{ height: LAYOUT.HEADER_HEIGHT }}
      >
        <RevyMark compact={collapsed} />
        {onToggle && (
          <TooltipIconButton
            label={t('navigation.toggleSidebar')}
            icon={(
              <PanelLeft
                className={cn('transition-transform duration-200', collapsed && 'rotate-180')}
                strokeWidth={1.75}
              />
            )}
            onClick={onToggle}
          />
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-3">
        {sections.map((section) => (
          <NavSection
            key={section.id}
            section={section}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <Separator />
      <div className="shrink-0 px-3 py-3">
        <NavSection section={footer} collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </div>
  );
};

export default SidebarPanel;
